'use client'

import posthog from 'posthog-js'

// Acquisition repos own first-touch attribution. The web app (web.roast.dating)
// inherits the resulting person properties once the user identifies — there's
// no point capturing UTMs post-funnel because the user is already past the
// channel by then. See PostHog playbook chapter 12.
//
// Two layers of capture:
//
//   1. Last-touch UTM (this session) → `posthog.register()` so every event
//      from now on carries the channel context. Useful for event-level
//      breakdowns and the `$pageview` insights that already exist.
//
//   2. First-touch attribution (lifetime) → `posthog.setPersonProperties`
//      with `$set_once`, so the very first channel/referrer/click ID/country
//      sticks on the person forever. PostHog merges this onto the identified
//      person when the user submits an email further down the funnel.
//
// Country comes from `/api/geo` (Vercel `x-vercel-ip-country` header). Client
// JS can't read its own IP — needs a server roundtrip — but it's a one-shot
// fetch on first visit, then cached in localStorage.

export type FunnelUtm = {
  utm_source?: string | null
  utm_medium?: string | null
  utm_campaign?: string | null
  utm_content?: string | null
  utm_term?: string | null
}

const FIRST_TOUCH_KEY = 'ph_initial_attribution_v1'

type FirstTouch = {
  initial_utm_source: string | null
  initial_utm_medium: string | null
  initial_utm_campaign: string | null
  initial_utm_content: string | null
  initial_utm_term: string | null
  initial_gclid: string | null
  initial_fbclid: string | null
  initial_ttclid: string | null
  initial_referrer: string | null
  initial_landing_url: string
  initial_landing_at: string
  initial_country: string | null
}

/**
 * Persist first-touch attribution into PostHog as `$set_once` person
 * properties, and add last-touch UTM as event-level via `register()`.
 *
 * Safe to call on every page load — the first-touch snapshot is gated by
 * localStorage so subsequent calls only refresh last-touch.
 *
 * `utm` is the already-resolved UTM tuple from `utm-tracker-client.tsx`
 * (URL params with referrer-based fallback). We trust that resolution and
 * just persist what's there.
 */
export function recordAcquisitionTouch(input: {
  utm: FunnelUtm | null
  referrer: string | null
  searchParams: URLSearchParams
}): void {
  if (typeof window === 'undefined') return

  const { utm, referrer, searchParams } = input
  const gclid = searchParams.get('gclid')
  const fbclid = searchParams.get('fbclid')
  const ttclid = searchParams.get('ttclid')

  // --- Last-touch: register on every event ----------------------------
  if (utm) {
    posthog.register({
      utm_source: utm.utm_source,
      utm_medium: utm.utm_medium,
      utm_campaign: utm.utm_campaign,
      utm_content: utm.utm_content,
      utm_term: utm.utm_term,
    })
  }
  if (gclid || fbclid || ttclid) {
    posthog.register({ gclid, fbclid, ttclid })
  }

  // --- First-touch: persist once, then mirror to person props ---------
  let existing: FirstTouch | null = null
  try {
    const raw = window.localStorage.getItem(FIRST_TOUCH_KEY)
    if (raw) existing = JSON.parse(raw) as FirstTouch
  } catch {
    // localStorage unavailable (privacy mode, etc.) — skip persistence.
  }
  if (existing) {
    // We've already recorded the first touch — nothing more to do. Country
    // and click IDs are part of the snapshot, so we don't re-fetch.
    return
  }

  const firstTouch: FirstTouch = {
    initial_utm_source: utm?.utm_source ?? null,
    initial_utm_medium: utm?.utm_medium ?? null,
    initial_utm_campaign: utm?.utm_campaign ?? null,
    initial_utm_content: utm?.utm_content ?? null,
    initial_utm_term: utm?.utm_term ?? null,
    initial_gclid: gclid,
    initial_fbclid: fbclid,
    initial_ttclid: ttclid,
    initial_referrer: referrer || null,
    initial_landing_url: window.location.pathname,
    initial_landing_at: new Date().toISOString(),
    initial_country: null, // filled in below once the geo fetch resolves
  }

  // Fire-and-forget country fetch. We don't block the first-touch save on
  // it — better to record everything we know now and patch country in.
  fetch('/api/geo', { cache: 'no-store' })
    .then((res) => (res.ok ? res.json() : null))
    .then((data: { country?: string | null } | null) => {
      const country = data?.country ?? null
      const snapshot: FirstTouch = { ...firstTouch, initial_country: country }
      try {
        window.localStorage.setItem(FIRST_TOUCH_KEY, JSON.stringify(snapshot))
      } catch {
        // best-effort
      }
      // `setPersonProperties(set, setOnce)` — only `setOnce` is used here so
      // each property locks to its first value across the person's lifetime.
      posthog.setPersonProperties(undefined, snapshot)
    })
    .catch(() => {
      // Geo failed — still persist what we have so we don't re-attempt on
      // every page load.
      try {
        window.localStorage.setItem(FIRST_TOUCH_KEY, JSON.stringify(firstTouch))
      } catch {
        // best-effort
      }
      posthog.setPersonProperties(undefined, firstTouch)
    })
}
